import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import Swal from "sweetalert2";

const ReviewStep = ({ data, prevStep, credits, userForceTest }) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewIsVideo, setPreviewIsVideo] = useState(false);
  const isVideoMedia = previewIsVideo || data.mediaMimeType === "video/mp4" || data.mediaMimeType === "video/quicktime" || /\.(mp4|mov|m4v)(\?|$)/i.test(data.mediaUrl || "");
  const [showLowBalanceModal, setShowLowBalanceModal] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const navigate = useNavigate();

  const complianceOption = data.complianceOption || "Reply STOP to unsubscribe";
  const complianceLength = (complianceOption === "none" || complianceOption === "None") ? 0 : complianceOption.length + 2;
  const count = data.contactListCount || 0;
  const baseLength = data.messageTemplate?.length || 0;
  const totalLength = baseLength > 0 ? baseLength + complianceLength : 0;
  const segmentSize = data.messageType === "mms" ? 1600 : 160;
  const segments = Math.ceil(totalLength / segmentSize) || 0;
  
  let costPerContact = 0;
  if (data.messageType === "mms") {
    costPerContact = segments * 8;
  } else {
    costPerContact = segments * 3;
  }
  
  const estimatedCost = count * costPerContact;

  const campaignDetails = {
    name: data.name || "Untitled Campaign",
    targetAudience: data.contactListName || "No Contact List Selected",
    contactListId: data.contactListId || null,
    messageTemplate: data.messageTemplate || "N/A",
  };

  const handleLaunchCampaign = async () => {
    try {
      if (!data.name || !data.contactListId || !data.messageTemplate) {
         return Swal.fire("Missing Fields", "Please ensure the Campaign Name, Contact List, and Message Template are all filled out.", "warning");
      }
      if (credits < estimatedCost) {
        setShowLowBalanceModal(true);
        return;
      }
      
      setIsLaunching(true);
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        name: data.name,
        contactListId: data.contactListId,
        messageBody: data.messageTemplate,
        draftId: data.draftId || null,
        campaignType: data.campaignType || "broadcast",
        complianceOption: data.complianceOption || "Reply STOP to unsubscribe",
        messagesPerNumber: (data.messagesPerNumber !== "" && data.messagesPerNumber !== undefined) ? parseInt(data.messagesPerNumber, 10) : undefined,
        phoneNumbers: data.phoneNumbers || [],
        // Use the proxy URL so Twilio can reliably fetch MMS media (direct Vercel Blob URLs
        // are not consistently reachable by Twilio's servers — see Error 12300)
        mediaUrl: data.mediaId
          ? `${API_BASE_URL}/media/proxy/${data.mediaId}`
          : (data.mediaUrl || null),
      };

      const response = await axios.post(`${API_BASE_URL}/campaigns`, payload, {
        headers: authHeaders
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Campaign Launched!",
          text: "Your campaign has been successfully inserted into the queue.",
          timer: 3000,
          showConfirmButton: false,
        });
        navigate("/admin/campaign-overview");
      }
    } catch (error) {
      console.error("Launch error:", error);
      Swal.fire("Launch Failed", error?.response?.data?.message || "Could not launch campaign.", "error");
    } finally {
      setIsLaunching(false);
    }
  };

  const handleBuyCredits = async (amount) => {
    setPurchasing(amount);
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      const draftPayload = {
        name: data.name,
        contactListId: data.contactListId,
        messageBody: data.messageTemplate,
        campaignType: data.campaignType || "broadcast",
        complianceOption: data.complianceOption || "Reply STOP to unsubscribe",
        isDraft: true,
        draftId: data.draftId || null,
        phoneNumbers: data.phoneNumbers || [],
        messagesPerNumber: data.messagesPerNumber !== "" ? data.messagesPerNumber : undefined,
        mediaUrl: data.mediaId
          ? `${API_BASE_URL}/media/proxy/${data.mediaId}`
          : (data.mediaUrl || null),
      };

      const draftRes = await axios.post(`${API_BASE_URL}/campaigns`, draftPayload, {
        headers: authHeaders
      });

      if (draftRes.data.success && draftRes.data.campaign) {
        const newDraftId = draftRes.data.campaign._id;
        
        const billingRes = await axios.post(`${API_BASE_URL}/billing/buy-credits`, {
          amount,
          successUrl: `${window.location.origin}/admin/create-campaign?draftId=${newDraftId}`
        }, { headers: authHeaders });

        if (billingRes.data.success) {
          window.location.href = billingRes.data.url;
        }
      }
    } catch (err) {
       console.error("Purchase error:", err);
       Swal.fire("Error", "Could not process top-up request.", "error");
       setPurchasing(null);
    }
  };

  return (
    <div className="px-3">
      <h4 style={{ fontWeight: "600" }} className="mt-0 mb-4">{campaignDetails.name}</h4>

      {userForceTest && (
        <div className="mb-4">
          {data.approvalStatus === "none" && (
            <div className="alert alert-warning py-3 px-4 d-flex align-items-center" style={{ borderRadius: "8px", border: "1px solid #ffeeba" }}>
              <i className="fas fa-exclamation-triangle mr-3" style={{ fontSize: "1.4rem", marginRight: "12px", color: "#856404" }}></i>
              <div>
                <strong className="d-block" style={{ color: "#856404" }}>Test Message Required</strong>
                <span className="small text-muted">You must send a test message from Step 2 to submit this campaign for administrator approval.</span>
              </div>
            </div>
          )}
          {data.approvalStatus === "pending" && (
            <div className="alert alert-info py-3 px-4 d-flex align-items-center" style={{ borderRadius: "8px", border: "1px solid #bee5eb" }}>
              <i className="fas fa-hourglass-half mr-3" style={{ fontSize: "1.4rem", marginRight: "12px", color: "#0c5460" }}></i>
              <div>
                <strong className="d-block" style={{ color: "#0c5460" }}>Pending Administrator Approval</strong>
                <span className="small text-muted">Your campaign test message is currently under review by administrators. You will be able to launch once approved.</span>
              </div>
            </div>
          )}
          {data.approvalStatus === "denied" && (
            <div className="alert alert-danger py-3 px-4 d-flex align-items-center" style={{ borderRadius: "8px", border: "1px solid #f5c6cb" }}>
              <i className="fas fa-times-circle mr-3" style={{ fontSize: "1.4rem", marginRight: "12px", color: "#721c24" }}></i>
              <div>
                <strong className="d-block" style={{ color: "#721c24" }}>Test Message Denied</strong>
                <span className="small text-muted">Administrators have rejected your test message. Please return to Step 2, update your message content, and send a new test.</span>
              </div>
            </div>
          )}
          {data.approvalStatus === "approved" && (
            <div className="alert alert-success py-3 px-4 d-flex align-items-center" style={{ borderRadius: "8px", border: "1px solid #c3e6cb" }}>
              <i className="fas fa-check-circle mr-3" style={{ fontSize: "1.4rem", marginRight: "12px", color: "#155724" }}></i>
              <div>
                <strong className="d-block" style={{ color: "#155724" }}>Test Message Approved</strong>
                <span className="small text-muted">Administrators have approved your test message. You are ready to launch!</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-3">
        <div style={{ fontWeight: "600", color: "#5E5E5E" }} className="fw-bold">Campaign Name</div>
        <div style={{ fontWeight: "600", color: "#5E5E5E" }}>{campaignDetails.name}</div>
      </div>

      <div className="mb-3">
        <div style={{ fontWeight: "600", color: "#5E5E5E" }} className="fw-bold">Contact List</div>
        <div style={{ fontWeight: "600", color: "#5E5E5E" }}>{campaignDetails.targetAudience} ({data.contactListCount || 0} contacts)</div>
      </div>

      <div className="mb-3">
        <div style={{ fontWeight: "600", color: "#5E5E5E" }} className="fw-bold">Message Template</div>
        <div style={{ fontWeight: "600", color: "#5E5E5E", wordBreak: "break-word" }}>{campaignDetails.messageTemplate}</div>
      </div>

      <div className="mb-3">
        <div style={{ fontWeight: "600", color: "#5E5E5E" }} className="fw-bold">Campaign Type</div>
        <div style={{ fontWeight: "600", color: "#5E5E5E" }}>
          {data.campaignType === "p2p" ? "P2P (Peer-to-Peer / Manual Send)" : "A2P (Automated Broadcast)"}
        </div>
      </div>

      <div className="mb-3">
        <div style={{ fontWeight: "600", color: "#5E5E5E" }} className="fw-bold">Number Rotation</div>
        <div style={{ fontWeight: "600", color: data.messagesPerNumber ? "#5E5E5E" : "#8E8E8E" }}>
          {data.messagesPerNumber
            ? `${data.messagesPerNumber} message${data.messagesPerNumber !== 1 ? "s" : ""} per number`
            : "Disabled (no rotation)"}
        </div>
      </div>

      <div className="mb-3 mt-4">
        <div style={{ fontWeight: "700", color: "#014c95", fontSize: "1.2rem" }}>Estimated Cost: ${(estimatedCost / 100).toFixed(2)}</div>
        <div style={{ fontWeight: "600", color: credits < estimatedCost ? "#dc3545" : "#28a745" }}>Your Balance: ${(credits / 100).toFixed(2)}</div>
      </div>

      <div
        className="p-3 rounded mt-4 mb-4"
        style={{
          backgroundColor: credits >= estimatedCost ? "#DFFFD9" : "#FFDCDC",
          border: credits >= estimatedCost ? "1px solid #b8e2b1" : "1px solid #e2b1b1",
          color: credits >= estimatedCost ? "#197a1f" : "#D10000",
        }}
      >
        <div style={{ fontWeight: "600" }} className="mb-1">
          {credits >= estimatedCost ? (
            <><i className="fas fa-check-circle me-2"></i> Ready to Launch</>
          ) : (
            <><i className="fas fa-exclamation-triangle me-2"></i> Insufficient Credits</>
          )}
        </div>
        <div style={{ fontSize: "0.9rem" }}>
          {credits >= estimatedCost 
            ? "Your Campaign has passed all checks and is ready to be launched. Please review the details above one final time before proceeding."
            : "You do not have enough credits to launch this campaign. Please top up your balance."}
        </div>
      </div>

      <div className="d-flex justify-content-between">
        <button style={{ background: "white", color: 'black', fontWeight: '700', border: "2px solid #c7c7c7" }} className="btn px-4" onClick={prevStep}>
          Back
        </button>
        <div>
          <button
            style={{ background: "white", color: 'black', fontWeight: '700', marginRight: "20px", border: "2px solid #c7c7c7" }} className="btn px-4"
            onClick={() => setShowPreviewModal(true)}
          >
            Show Preview
          </button>
          <button style={{ background: '#004992' }}
            className="btn px-4"
            onClick={handleLaunchCampaign}
            disabled={isLaunching || (userForceTest && data.approvalStatus !== "approved")}
          >
            {isLaunching
              ? "Launching..."
              : userForceTest && data.approvalStatus === "pending"
              ? "Pending Approval"
              : userForceTest && data.approvalStatus === "denied"
              ? "Denied"
              : "Launch"}
          </button>
        </div>
      </div>
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        centered
        dialogClassName="custom-modal"
        style={{ borderRadius: "12px" }}
      >
        <Modal.Header style={{ padding: '10px' }} closeButton>
        </Modal.Header>
        <Modal.Title style={{ fontSize: "2.2rem", fontWeight: "700", margin: "0 0 10px 0", paddingLeft: '24px' }}>Campaign Preview</Modal.Title>

        <Modal.Body>
          <div
            className="d-flex justify-content-end align-items-center"
            style={{ minHeight: "200px", border: "2px solid #c7c7c7", borderRadius: '10px', padding: '15px' }}
          >
            <div
              className="bg-primary text-white p-3"
              style={{
                maxWidth: "300px",
                borderRadius: "12px",
                fontSize: "0.9rem",
              }}
            >
              {data.mediaUrl && (
                <div className="mb-2">
                  {isVideoMedia ? (
                    <video
                      src={data.mediaUrl}
                      controls
                      muted
                      playsInline
                      style={{ width: "100%", maxHeight: "160px", borderRadius: "8px", backgroundColor: "#000" }}
                    />
                  ) : (
                    <img
                      src={data.mediaUrl}
                      alt="Campaign Media Preview"
                      onError={() => setPreviewIsVideo(true)}
                      style={{ width: "100%", maxHeight: "160px", objectFit: "contain", borderRadius: "8px" }}
                    />
                  )}
                </div>
              )}
              {data.messageTemplate ||
                `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor.`}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          {/* Back Button on the left */}
          <Button
            variant="light"
            className="border px-4"
            style={{
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "8px 25px",
              fontWeight: "600",
              color: 'black'
            }}
            onClick={() => setShowPreviewModal(false)}
          >
            Back
          </Button>

          {/* Send Button on the right */}
          <Button
            variant="primary"
            className="px-4"
            style={{
              backgroundColor: "#004992",
              border: "none",
              fontWeight: "600",
              borderRadius: "8px",
              padding: "8px 25px",
            }}
            onClick={() => alert("Test message sent!")}
          >
            Send
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showLowBalanceModal}
        onHide={() => setShowLowBalanceModal(false)}
        centered
        dialogClassName="custom-modal"
        style={{ borderRadius: "12px" }}
      >
        <Modal.Header closeButton style={{ padding: '20px 24px', borderBottom: 'none' }}>
          <Modal.Title style={{ fontSize: "1.8rem", fontWeight: "700", margin: 0 }}>Insufficient Credits</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0 24px 24px' }}>
          <p style={{ fontSize: '1rem', color: '#5E5E5E', marginBottom: '20px' }}>
            Your estimated cost is <strong>${(estimatedCost / 100).toFixed(2)}</strong> but your balance is only <strong>${(credits / 100).toFixed(2)}</strong>. Please purchase a credit package to launch this campaign.
          </p>
          <div className="d-grid gap-3">
            {[25, 50, 100].map(amount => (
              <Button
                key={amount}
                variant="outline-primary"
                className="py-3 fw-bold w-100"
                style={{ borderRadius: "8px", border: "2px solid #014c95", color: "#014c95", backgroundColor: "white", marginBottom: "10px" }}
                onClick={() => handleBuyCredits(amount)}
                disabled={purchasing !== null}
              >
                {purchasing === amount ? "Processing..." : `Buy $${amount} Package`}
              </Button>
            ))}
          </div>
        </Modal.Body>
      </Modal>


    </div>
  );
};

export default ReviewStep;
